import { Flex, Image, Text } from "@chakra-ui/react";
import { memo } from "react";
import { Trans } from "react-i18next";
import { useRecoilValue } from "recoil";

import ButtonSection from "components/Common/ShopItem/ButtonSection";
import { PriceFormat } from "components/Common/ShopItem/PriceFormat";
import useShopItemHook from "components/Common/ShopItem/hooks";
import { ConsultTag } from "components/ConsultTag";
import useProductLocalization from "components/hooks/Localization/useProductLocalization";
import { OrderModel, PlatformType, StripeProductLocaleKeys } from "graphql/generated";
import { track } from "src/dataTracking";
import { TRACKING_EVENTS } from "src/dataTracking/events";
import { patientState } from "src/states";
import { getImageUrlFromImageModel } from "src/utils";
import getNameProp from "src/utils/getNameProp";

interface Props {
  t: (key: string) => string;
  name?: string;
  description?: string;
  wrapperStyle?: any;
  descriptionWrapperMinH?: any;
  isAdded?: boolean;
  [productProps: string]: any;
  hideBuyButton?: boolean;
  fromThankYou?: boolean;
  isSuggestion?: boolean;
  customButtonSection?: React.ReactNode;
  teleConsultOrders: OrderModel[];
  excludedEvalIds?: string[];
}

const ShopItem = ({
  t,
  wrapperStyle,
  descriptionWrapperMinH,
  isAdded,
  hideBuyButton = false,
  fromThankYou = false,
  isSuggestion = false,
  customButtonSection,
  teleConsultOrders = [],
  excludedEvalIds,
  ...productProps
}: Props) => {
  const {
    name,
    image,
    id,
    metadata: { requires_prescription: requiresPrescription = true, category = [], sku = "" }
  } = productProps;

  const { getTranslatedText } = useProductLocalization();

  const { isMobile, setProductDetailState, setIsDetailModalOpened, fallbackPrice, platform } =
    useShopItemHook();

  const patient = useRecoilValue(patientState);

  return (
    <Flex
      bg="white"
      borderRadius="8px"
      direction="column"
      w="auto"
      h="100%"
      minH={[100, 430, 430]}
      padding={{ base: "10px", md: "20px" }}
      boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
      justifyContent="space-between"
      {...getNameProp("product-card")}
      {...wrapperStyle}
    >
      <Flex direction="column" alignItems="space-between" mb="4px" flex={1}>
        <Flex
          direction="column"
          flex={1}
          minH={descriptionWrapperMinH ?? { base: "235px", md: "320px" }}
        >
          <Image
            w="auto"
            h={{ base: "90px", md: "140px" }}
            src={getImageUrlFromImageModel(image, platform, false)}
            alt={name}
            objectFit="cover"
            mb={{ base: "10px", md: "20px" }}
            borderRadius="8px"
          />
          <Text
            fontFamily="heading"
            fontWeight="500"
            fontSize={["18px", "24px"]}
            mb={{ base: "4px", md: "8px" }}
            color={platform === "zoey" ? "primary.500" : "inherit"}
            {...getNameProp("product-menu")}
          >
            <Trans
              components={{
                br: <br />
              }}
            >
              {getTranslatedText(id, StripeProductLocaleKeys.ProductName)}
            </Trans>
          </Text>
          <Text
            fontSize={{ base: "xs", md: "sm" }}
            maxHeight="normal"
            lineHeight="normal"
            mb={{ base: "8px", md: "12px" }}
            {...getNameProp("product-description")}
          >
            <Trans
              components={{
                br: <br />
              }}
            >
              {getTranslatedText(id, StripeProductLocaleKeys.ProductDescription)}
            </Trans>
          </Text>
          <Flex
            alignItems="flex-end"
            mb="5px"
            cursor="pointer"
            onClick={() => {
              setProductDetailState(productProps);
              setIsDetailModalOpened(true);
              const trackingMetadata = {
                "Product Name": name,
                "Product Category": category?.split(","),
                Prescription: requiresPrescription,
                "Product SKU": sku,
                "Suggested Product": isSuggestion
              };
              track(TRACKING_EVENTS.VIEW_DETAIL, trackingMetadata, patient);
            }}
            {...getNameProp("product-learn-more")}
          >
            <Text
              fontSize={{ base: "8px", md: "12px" }}
              lineHeight="normal"
              color={platform === PlatformType.Noah ? "primary.500" : "primaryTwo.500"}
              textDecoration="underline"
              fontWeight="semibold"
              pt="2px"
            >
              {t("shop:shop-item-learn-more")}
            </Text>
          </Flex>
        </Flex>

        <Flex direction="column">
          {requiresPrescription && !isMobile && (
            <Flex mb="12px">
              <ConsultTag isMobile={isMobile}>{t("shop:prescription-only")}</ConsultTag>
            </Flex>
          )}
          <PriceFormat displayId={id} fallbackPrice={fallbackPrice} t={t} />
        </Flex>
      </Flex>
      {isAdded ? (
        <Text {...getNameProp("added-label")}>{t("shop:added-to-cart-label")}</Text>
      ) : (
        customButtonSection || (
          <ButtonSection
            t={t}
            requiresPrescription={requiresPrescription}
            productProps={productProps}
            hideBuyButton={hideBuyButton}
            fromThankYou={fromThankYou}
            isSuggestion={isSuggestion}
            teleConsultOrders={teleConsultOrders}
            excludedEvalIds={excludedEvalIds}
          />
        )
      )}
    </Flex>
  );
};

export default memo(ShopItem);
